import { USDC_DECIMALS } from "@decibeltrade/sdk";

import { getChainNameFromDomainId } from "./domains";

/**
 * Parsed fields from a raw CCTP message hex string.
 *
 * CCTP message layout:
 *   Header (116 bytes): version(4) + sourceDomain(4) + destDomain(4) + nonce(8)
 *                        + sender(32) + recipient(32) + destCaller(32)
 *   BurnMessage body: version(4) + burnToken(32) + mintRecipient(32) + amount(32) + messageSender(32)
 */
export interface ParsedCCTPMessage {
  sourceDomain: number;
  sourceChain: string | null;
  destDomain: number;
  nonce: number;
  mintRecipient: string;
  amount: number;
}

const HEX_RE = /^[0-9a-fA-F]+$/;
const MESSAGE_VERSION_V1 = "00000000";

/**
 * Parses all relevant fields from a raw CCTP message hex string in one pass.
 * Returns null if the message is too short or contains invalid data.
 */
export function parseCCTPMessage(messageHex: string): ParsedCCTPMessage | null {
  const hex = messageHex.startsWith("0x") ? messageHex.slice(2) : messageHex;

  // Minimum length: 432 hex chars (216 bytes) to cover through the amount field
  if (hex.length < 432) return null;

  // A v2 message is long enough to pass the length check, but its fields sit at other offsets.
  if (hex.slice(0, 8) !== MESSAGE_VERSION_V1) return null;

  // sourceDomain: uint32 at byte 4 (hex 8), 4 bytes
  const sourceDomainHex = hex.slice(8, 16);
  if (sourceDomainHex.length !== 8 || !HEX_RE.test(sourceDomainHex)) return null;
  const sourceDomain = parseInt(sourceDomainHex, 16);

  // destDomain: uint32 at byte 8 (hex 16), 4 bytes
  const destDomainHex = hex.slice(16, 24);
  if (destDomainHex.length !== 8 || !HEX_RE.test(destDomainHex)) return null;
  const destDomain = parseInt(destDomainHex, 16);

  // nonce: uint64 at byte 12 (hex 24), 8 bytes
  const nonceHex = hex.slice(24, 40);
  if (nonceHex.length !== 16 || !HEX_RE.test(nonceHex)) return null;
  const nonce = Number(BigInt("0x" + nonceHex));

  // mintRecipient: 32 bytes in BurnMessage body at byte 152 (hex 304)
  const recipientHex = hex.slice(304, 368);
  if (recipientHex.length !== 64 || !HEX_RE.test(recipientHex)) return null;
  const mintRecipient = `0x${recipientHex.toLowerCase()}`;

  // amount: uint256 at byte 184 (hex 368), 32 bytes
  const amountHex = hex.slice(368, 432);
  if (amountHex.length !== 64 || !HEX_RE.test(amountHex)) return null;
  const USDC_UNIT = BigInt(10 ** USDC_DECIMALS);
  const raw = BigInt("0x" + amountHex);
  const amount = Number(raw / USDC_UNIT) + Number(raw % USDC_UNIT) / 10 ** USDC_DECIMALS;

  const sourceChain = getChainNameFromDomainId(sourceDomain);

  return { sourceDomain, sourceChain, destDomain, nonce, mintRecipient, amount };
}
